import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useEventStore } from '@/stores/useEventStore';
import { MeetingRequest } from '@/types/event';
import { formatDateTime } from '@/utils/formatDateTime';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

interface ReplanMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingRequest: MeetingRequest | null;
}

export const ReplanMeetingModal = ({
  isOpen,
  onClose,
  meetingRequest,
}: ReplanMeetingModalProps) => {
  const { t } = useTranslation();
  const { event, replanMeetingRequest } = useEventStore();

  type FormValues = {
    date: string;
  };

  if (!meetingRequest || !event) return null;

  const initialValues: FormValues = {
    date: meetingRequest.meeting_date,
  };

  const validationSchema = Yup.object().shape({
    date: Yup.date()
      .nullable()
      .required(t('common:validation.required'))
      .min(
        event.date,
        t('common:validation.date.min', {
          min: formatDateTime(event.date),
        }),
      )
      .test(
        'is-before-end-date',
        t('common:validation.date.max', {
          max: event.end_date ? formatDateTime(event.end_date) : '',
        }),
        function (value) {
          if (!event.end_date || !value) return true;
          return new Date(value) <= new Date(event.end_date);
        },
      ),
  });

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting, setErrors }: FormikHelpers<FormValues>,
  ) => {
    try {
      await replanMeetingRequest(meetingRequest.id, values.date);
      onClose();
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('eventDetails:meetingRequests.replanModal.title')}
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md"
    >
      <Text className="mb-4 text-gray-500">
        {t('eventDetails:meetingRequests.replanModal.description')}
      </Text>

      <div className="mb-4 rounded-lg bg-gray-50 p-3">
        <Text className="text-sm font-medium text-gray-700">
          {t('eventDetails:meetingRequests.replanModal.currentMeeting')}
        </Text>
        <Text className="text-sm text-gray-600">
          {t('eventDetails:meetingRequests.sender')}:{' '}
          {meetingRequest.sender.firstname} {meetingRequest.sender.lastname}
        </Text>
        <Text className="text-sm text-gray-600">
          {t('eventDetails:meetingRequests.recipient')}:{' '}
          {meetingRequest.recipient.firstname}{' '}
          {meetingRequest.recipient.lastname}
        </Text>
        <Text className="text-sm text-gray-600">
          {t('eventDetails:meetingRequests.meetingDate')}:{' '}
          {formatDateTime(meetingRequest.meeting_date)}
        </Text>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-4">
            <TextInput
              label={t('eventDetails:meetingRequests.replanModal.newDate')}
              name="date"
              type="datetime-local"
              min={event.date}
              max={event.end_date}
              isRequired
            />
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t('common:cancel')}
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                className="rounded-md bg-linko-light px-4 py-2 text-sm text-white hover:bg-linko-dark"
              >
                {t('eventDetails:meetingRequests.replanModal.confirm')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
